import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";

import { parseDate } from "../../helpers/date";
import {
  useAnalyzeClaimMutation,
  useAnalyzeDifferencesMutation,
  useUpdateResolutionStatusMutation,
} from "../../store/queries/claims";
import {
  IAIAnalysisResult,
  IAILineItemDiscrepancy,
  IPromptVersionSummary,
  ResolutionStatus,
  IResolutionHistoryEntry,
  RESOLUTION_STATUS_LABELS,
  RESOLUTION_STATUS_COLORS,
} from "../../types/claim";

interface IAIInsightsTabContentProps {
  claimId: string;
  cachedAnalysis: IAIAnalysisResult | null;
  promptVersion?: IPromptVersionSummary;
  claimData: {
    patientName: string;
    claimNumber: string | null;
    serviceDate: Date;
    claimPayerName: string;
    chargeAmount: number | null;
  };
  // Resolution status props
  resolutionStatus: ResolutionStatus | null;
  resolutionHistory?: IResolutionHistoryEntry[];
}

const ContentWrapper = styled.div`
  padding: 1.5rem;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
`;

const LoadingText = styled.div`
  color: #6c757d;
  font-size: 14px;
`;

const SectionHeader = styled.h3`
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
`;

const DetailRow = styled.div`
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  min-width: 200px;
  color: #495057;
`;

const DetailValue = styled.div`
  color: #212529;
`;

const VarianceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background-color: ${(props: { isNegative?: boolean }) =>
    props.isNegative ? "#fff3cd" : "#d1ecf1"};
  border-radius: 4px;
  margin-bottom: 0.5rem;
  font-size: 14px;
`;

const VarianceLabel = styled.div`
  font-weight: 600;
  color: #495057;
`;

const VarianceValue = styled.div`
  font-weight: 600;
  color: ${(props: { isNegative?: boolean }) =>
    props.isNegative ? "#856404" : "#0c5460"};
`;

const TimestampText = styled.div`
  color: #6c757d;
  font-size: 13px;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

const RegenerateButtonWrapper = styled.div`
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
  text-align: center;
`;

const ExplanatoryText = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-left: 3px solid #007bff;
  color: #495057;
  font-size: 14px;
  line-height: 1.5;
`;

// New styled components for v0-inspired design
const AnalysisCard = styled.div`
  overflow: hidden;
  margin-bottom: 2rem;
`;

const AnalysisCardHeader = styled.div`
  padding: 0 0 2rem 0;
  border-bottom: 1px solid #e9ecef;
`;

const AnalysisCardBody = styled.div`
  padding: 2rem 0 0 0;
`;

const AnalysisTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 0.5rem 0;
`;

const AnalysisSubtitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 500;
  color: #1d1d1f;
  margin: 0 0 0.5rem 0;
`;

const AnalysisDescription = styled.p`
  color: #86868b;
  line-height: 1.6;
  margin: 0.5rem 0 0 0;
`;

const LineItemSubheader = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 1rem 0;
`;

const LineItemCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid #e9ecef;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const LineItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LineItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ModifierBadge = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: #f5f5f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: #1d1d1f;
  flex-shrink: 0;
`;

const LineItemInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const LineItemCode = styled.div`
  font-weight: 600;
  font-size: 1.125rem;
  color: #1d1d1f;
`;

const LineItemAdjustment = styled.div`
  font-size: 0.875rem;
  color: #86868b;
`;

const LineItemAmounts = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  text-align: right;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const AmountColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const AmountLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 500;
  color: #86868b;
  margin-bottom: 0.25rem;
`;

const AmountValue = styled.div<{ isNegative?: boolean }>`
  font-weight: 500;
  color: ${(props) => (props.isNegative ? "#ff3b30" : "#1d1d1f")};
`;

const AmountDivider = styled.div`
  height: 2rem;
  width: 1px;
  background-color: #e9ecef;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LineItemExplanation = styled.div`
  font-size: 0.875rem;
  color: #86868b;
  line-height: 1.6;
  background-color: #f5f5f7;
  padding: 1rem;
  border-radius: 12px;
`;

const ResolutionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ResolutionPlanCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ResolutionPlanHeader = styled.div``;

const ResolutionPlanTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 0.5rem 0;
`;

const ResolutionPlanSubtitle = styled.p`
  color: #86868b;
  margin: 0;
`;

const ResolutionStepsList = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  &::before {
    content: "";
    position: absolute;
    left: 15px;
    top: 0.5rem;
    bottom: 0.5rem;
    width: 1px;
    background-color: #e9ecef;
  }
`;

const ResolutionStep = styled.div`
  position: relative;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  cursor: default;
  transition: all 0.2s;

  &:hover .step-circle {
    border-color: #0066cc;
    color: #0066cc;
  }
`;

const StepCircle = styled.div`
  position: relative;
  z-index: 10;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: white;
  border: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  color: #86868b;
  flex-shrink: 0;
  transition: all 0.2s;
`;

const StepText = styled.p`
  color: #1d1d1f;
  font-weight: 500;
  margin: 0;
  padding-top: 0.25rem;
`;

const AppealLetterCard = styled.div`
  background: white;
  border-radius: 24px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid #e9ecef;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const AppealLetterHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AppealLetterTitle = styled.h3`
  font-weight: 600;
  color: #1d1d1f;
  font-size: 1.125rem;
  margin: 0;
`;

const AppealLetterActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const AppealLetterBody = styled.div`
  padding: 2rem 2rem 3rem 2rem;
  background: white;
  flex-grow: 1;
  overflow: auto;
`;

const AppealLetterContent = styled.div`
  font-family: Georgia, serif;
  color: #1d1d1f;
  line-height: 1.6;
  white-space: pre-wrap;
  font-size: 0.9375rem;
`;

const DifferenceSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e9ecef;
`;

const DifferenceSectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 0.5rem 0;
`;

const DifferenceSectionDescription = styled.p`
  color: #86868b;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const DifferenceAnalysisBox = styled.div`
  padding: 0.75rem 1rem;
  background-color: #fff8e1;
  border-left: 3px solid #ffa000;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
`;

const DifferenceAnalysisText = styled.div`
  color: #333;
  white-space: pre-wrap;
`;

// Status options for dropdown
const StatusOptions = [
  {
    label: RESOLUTION_STATUS_LABELS[ResolutionStatus.IN_REVIEW],
    value: ResolutionStatus.IN_REVIEW,
  },
  {
    label: RESOLUTION_STATUS_LABELS[ResolutionStatus.SUBMITTED],
    value: ResolutionStatus.SUBMITTED,
  },
  {
    label: RESOLUTION_STATUS_LABELS[ResolutionStatus.CLOSED],
    value: ResolutionStatus.CLOSED,
  },
  {
    label: RESOLUTION_STATUS_LABELS[ResolutionStatus.NO_APPEAL_NEEDED],
    value: ResolutionStatus.NO_APPEAL_NEEDED,
  },
];

/**
 * Component to display AI-powered claim analysis
 */
export function AIInsightsTabContent({
  claimId,
  cachedAnalysis,
  promptVersion,
  claimData,
  resolutionStatus,
  resolutionHistory,
}: IAIInsightsTabContentProps) {
  const toast = useRef<Toast>(null);

  const [analyzeClaim, { data: analysisData, isLoading, isError, error }] =
    useAnalyzeClaimMutation();

  const [updateResolutionStatus, { isLoading: isUpdatingStatus }] =
    useUpdateResolutionStatusMutation();

  // AI difference analysis (similar claims comparison)
  const [
    analyzeDifferences,
    {
      data: differenceData,
      isLoading: isDifferenceLoading,
      isError: isDifferenceError,
    },
  ] = useAnalyzeDifferencesMutation();
  const [hasDifferenceTriggered, setHasDifferenceTriggered] = useState(false);

  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);
  const [copied, setCopied] = useState(false);

  // Status management state
  // Local state to reflect updates immediately without needing to close/reopen modal
  const [currentStatus, setCurrentStatus] = useState<ResolutionStatus | null>(
    resolutionStatus
  );
  const [currentHistory, setCurrentHistory] = useState<
    IResolutionHistoryEntry[]
  >(resolutionHistory || []);
  const [selectedStatus, setSelectedStatus] = useState<ResolutionStatus | null>(
    null
  );
  const [statusNotes, setStatusNotes] = useState("");
  const [showStatusForm, setShowStatusForm] = useState(false);

  // Auto-trigger analysis on first mount if no cached analysis
  useEffect(() => {
    if (!cachedAnalysis && !hasAutoTriggered && !isLoading) {
      setHasAutoTriggered(true);
      analyzeClaim({ claimId, regenerate: false });
    }
  }, [cachedAnalysis, hasAutoTriggered, isLoading, claimId, analyzeClaim]);

  // Auto-trigger difference analysis on mount (only once)
  useEffect(() => {
    if (!hasDifferenceTriggered) {
      setHasDifferenceTriggered(true);
      analyzeDifferences({ claimId });
    }
  }, [hasDifferenceTriggered, claimId, analyzeDifferences]);

  // Determine which analysis to display
  const analysis: IAIAnalysisResult | null =
    cachedAnalysis || analysisData?.analysis || null;

  // Format currency (cents to dollars)
  const formatCurrency = (cents: number): string => {
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Format dollars (AI analysis amounts are already in dollars, not cents)
  const formatDollars = (dollars: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Get confidence tag severity
  const getConfidenceSeverity = (
    confidence: "High" | "Medium" | "Low"
  ): "success" | "warning" | "danger" => {
    if (confidence === "High") return "success";
    if (confidence === "Medium") return "warning";
    return "danger";
  };

  // Handle regenerate button click
  const handleRegenerate = () => {
    analyzeClaim({ claimId, regenerate: true });
  };

  // Handle copy to clipboard
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;

    try {
      const result = await updateResolutionStatus({
        claimId,
        status: selectedStatus,
        notes: statusNotes || undefined,
      }).unwrap();

      // Update local state immediately to reflect changes without needing to close/reopen modal
      setCurrentStatus(result.resolutionStatus);
      setCurrentHistory(result.resolutionHistory || []);

      toast.current?.show({
        severity: "success",
        summary: "Status Updated",
        detail: `Status changed to "${RESOLUTION_STATUS_LABELS[selectedStatus]}"`,
      });

      // Reset form
      setSelectedStatus(null);
      setStatusNotes("");
      setShowStatusForm(false);
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (err as any)?.data?.error || "Failed to update status",
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <LoadingWrapper>
        <ProgressSpinner
          style={{ width: "50px", height: "50px" }}
          strokeWidth="4"
        />
        <LoadingText>
          Analyzing this claim with AI to determine possible causes...
        </LoadingText>
        <LoadingText style={{ fontSize: "12px", color: "#999" }}>
          This may take up to 60 seconds
        </LoadingText>
      </LoadingWrapper>
    );
  }

  // Error state
  if (isError && !analysis) {
    const errorMessage =
      (error as any)?.data?.error ||
      "An error occurred during analysis. Please try again.";

    return (
      <ContentWrapper>
        <Message
          severity="error"
          text={errorMessage}
          style={{ width: "100%" }}
        />
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <Button
            label="Retry Analysis"
            icon="pi pi-refresh"
            onClick={handleRegenerate}
            className="p-button-danger"
          />
        </div>
      </ContentWrapper>
    );
  }

  // No analysis available (shouldn't happen due to auto-trigger)
  if (!analysis) {
    return (
      <ContentWrapper>
        <Message
          severity="info"
          text="No analysis available yet"
          style={{ width: "100%" }}
        />
      </ContentWrapper>
    );
  }

  // Success state - display analysis
  return (
    <ContentWrapper>
      <Toast ref={toast} />

      {/* Summary Section */}
      <div style={{ marginBottom: "2rem" }}>
        <AnalysisTitle>Summary</AnalysisTitle>
        <ExplanatoryText>{analysis.summary}</ExplanatoryText>
      </div>

      {/* Two-column layout for Claim Summary and Effective Totals */}
      <div
        style={{
          display: "flex",
          gap: "2rem",
          marginBottom: "2rem",
          flexWrap: "wrap",
        }}
      >
        {/* Left Column: Claim Summary */}
        <div style={{ flex: "1 1 275px", minWidth: 0 }}>
          <AnalysisTitle style={{ marginBottom: "1rem" }}>
            Claim Summary
          </AnalysisTitle>
          <DetailRow>
            <DetailLabel>Patient Name:</DetailLabel>
            <DetailValue data-pii="patient-name">
              {claimData.patientName}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Claim Number:</DetailLabel>
            <DetailValue>{claimData.claimNumber || "-"}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Service Date:</DetailLabel>
            <DetailValue>{parseDate(claimData.serviceDate)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Payer Name:</DetailLabel>
            <DetailValue>{claimData.claimPayerName}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Total Charge:</DetailLabel>
            <DetailValue>
              {claimData.chargeAmount
                ? formatCurrency(claimData.chargeAmount)
                : "-"}
            </DetailValue>
          </DetailRow>
        </div>

        {/* Right Column: Effective Totals */}
        <div style={{ flex: "1 1 275px", minWidth: 0 }}>
          <SectionHeader>Effective Totals</SectionHeader>
          <VarianceRow>
            <VarianceLabel>Effective Expected:</VarianceLabel>
            <VarianceValue>
              {formatDollars(analysis.effectiveTotals.effectiveExpectedAllowed)}
            </VarianceValue>
          </VarianceRow>
          <VarianceRow>
            <VarianceLabel>Effective Actual:</VarianceLabel>
            <VarianceValue>
              {formatDollars(analysis.effectiveTotals.effectiveActualAllowed)}
            </VarianceValue>
          </VarianceRow>
          <VarianceRow
            isNegative={analysis.effectiveTotals.underpaymentAmount < 0}
          >
            <VarianceLabel>
              {analysis.effectiveTotals.underpaymentAmount < 0
                ? "Overpayment:"
                : "Underpayment:"}
            </VarianceLabel>
            <VarianceValue
              isNegative={analysis.effectiveTotals.underpaymentAmount < 0}
            >
              {analysis.effectiveTotals.underpaymentAmount < 0
                ? formatDollars(
                    Math.abs(analysis.effectiveTotals.underpaymentAmount)
                  )
                : formatDollars(analysis.effectiveTotals.underpaymentAmount)}
            </VarianceValue>
          </VarianceRow>
          <VarianceRow isNegative={analysis.variance.variance < 0}>
            <VarianceLabel>Percent Difference:</VarianceLabel>
            <VarianceValue isNegative={analysis.variance.variance < 0}>
              {analysis.variance.percentDifference.toFixed(2)}%
            </VarianceValue>
          </VarianceRow>
        </div>
      </div>

      {/* Combined Analysis Section - Classifications + Line Items */}
      {analysis.classifications.length > 0 && (
        <AnalysisCard>
          <AnalysisCardHeader>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem",
                marginBottom: "1.5rem",
                flexWrap: "wrap",
              }}
            >
              <AnalysisTitle>Analysis</AnalysisTitle>
              <Tag
                value={`${analysis.classifications[0].confidence} Confidence`}
                severity={getConfidenceSeverity(
                  analysis.classifications[0].confidence
                )}
                style={{ fontSize: "14px", padding: "0.5rem 1rem" }}
              />
            </div>
            <AnalysisSubtitle>
              {analysis.classifications[0].title}
            </AnalysisSubtitle>
            <AnalysisDescription>
              {analysis.classifications[0].explanation}
            </AnalysisDescription>
          </AnalysisCardHeader>

          {/* Line Item Details Section */}
          {analysis.lineItemDiscrepancies &&
            analysis.lineItemDiscrepancies.length > 0 && (
              <AnalysisCardBody>
                <LineItemSubheader>Line Item Details</LineItemSubheader>
                <div>
                  {analysis.lineItemDiscrepancies.map(
                    (item: IAILineItemDiscrepancy, index: number) => (
                      <LineItemCard key={index}>
                        <LineItemHeader>
                          <LineItemLeft>
                            <ModifierBadge>
                              {item.modifier || "—"}
                            </ModifierBadge>
                            <LineItemInfo>
                              <LineItemCode>{item.cpt}</LineItemCode>
                              <LineItemAdjustment>
                                Adjustment Code:{" "}
                                {item.adjustmentCodes?.join(", ") || "N/A"}
                              </LineItemAdjustment>
                            </LineItemInfo>
                          </LineItemLeft>
                          <LineItemAmounts>
                            <AmountColumn>
                              <AmountLabel>Expected</AmountLabel>
                              <AmountValue>
                                {formatDollars(item.expectedAllowed)}
                              </AmountValue>
                            </AmountColumn>
                            <AmountDivider />
                            <AmountColumn>
                              <AmountLabel>Actual</AmountLabel>
                              <AmountValue
                                isNegative={item.actualAllowed === 0}
                              >
                                {formatDollars(item.actualAllowed)}
                              </AmountValue>
                            </AmountColumn>
                          </LineItemAmounts>
                        </LineItemHeader>
                        <LineItemExplanation>
                          {item.explanation}
                        </LineItemExplanation>
                      </LineItemCard>
                    )
                  )}
                </div>
              </AnalysisCardBody>
            )}
        </AnalysisCard>
      )}

      {/* Resolution Section with Two-Column Layout */}
      <ResolutionGrid>
        {/* Left Column: Resolution Plan */}
        <ResolutionPlanCard>
          <ResolutionPlanHeader>
            <ResolutionPlanTitle>Resolution Plan</ResolutionPlanTitle>
            <ResolutionPlanSubtitle>
              Follow these steps to resolve the underpayment.
            </ResolutionPlanSubtitle>
          </ResolutionPlanHeader>

          {/* Resolution Steps with Vertical Line */}
          <ResolutionStepsList>
            <ResolutionStep>
              <StepCircle className="step-circle">1</StepCircle>
              <StepText>Capture contract calculation screenshot</StepText>
            </ResolutionStep>
            <ResolutionStep>
              <StepCircle className="step-circle">2</StepCircle>
              <StepText>Capture remittance (835/RA) details</StepText>
            </ResolutionStep>
            <ResolutionStep>
              <StepCircle className="step-circle">3</StepCircle>
              <StepText>Attach original claim form</StepText>
            </ResolutionStep>
            <ResolutionStep>
              <StepCircle className="step-circle">4</StepCircle>
              <StepText>Submit appeal with evidence</StepText>
            </ResolutionStep>
          </ResolutionStepsList>

          {/* Resolution Status */}
          <div
            style={{
              marginTop: "1.5rem",
              paddingTop: "1.5rem",
              borderTop: "1px solid #e9ecef",
            }}
          >
            {/* Current Status */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "1rem",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontWeight: 500, color: "#495057" }}>
                Current Status:
              </span>
              <Tag
                value={RESOLUTION_STATUS_LABELS[currentStatus || "null"]}
                severity={RESOLUTION_STATUS_COLORS[currentStatus || "null"]}
              />
            </div>

            {/* Status History (oldest to newest) */}
            {currentHistory && currentHistory.length > 0 && (
              <div style={{ marginBottom: "1rem" }}>
                {currentHistory.map((entry, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "0.75rem",
                      backgroundColor: index % 2 === 0 ? "#f8f9fa" : "white",
                      borderRadius: "4px",
                      marginBottom: "0.25rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    <div style={{ color: "#495057" }}>
                      <span style={{ fontWeight: 500 }}>
                        {RESOLUTION_STATUS_LABELS[
                          entry.status as ResolutionStatus
                        ] || entry.status}
                      </span>
                      <span style={{ color: "#86868b" }}>
                        {" "}
                        — {entry.userName},{" "}
                        {new Date(entry.timestamp).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}{" "}
                        at{" "}
                        {new Date(entry.timestamp).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    {entry.notes && (
                      <div
                        style={{
                          marginTop: "0.5rem",
                          color: "#495057",
                          fontStyle: "italic",
                        }}
                      >
                        &quot;{entry.notes}&quot;
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Update Status Button / Form */}
            {!showStatusForm ? (
              <Button
                label="Update Status"
                icon="pi pi-pencil"
                className="p-button-outlined"
                onClick={() => setShowStatusForm(true)}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  padding: "1rem",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#495057",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Update Status
                  </div>
                  <Dropdown
                    inputId="status-select"
                    value={selectedStatus}
                    options={StatusOptions}
                    onChange={(e) => setSelectedStatus(e.value)}
                    placeholder="Select new status"
                    style={{ minWidth: "220px" }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="status-notes"
                    style={{
                      display: "block",
                      fontWeight: 500,
                      color: "#495057",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Notes (optional):
                  </label>
                  <InputTextarea
                    id="status-notes"
                    value={statusNotes}
                    onChange={(e) => setStatusNotes(e.target.value)}
                    rows={2}
                    placeholder="Add notes about this status change..."
                    style={{ width: "100%" }}
                  />
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Button
                    label="Save"
                    icon="pi pi-check"
                    onClick={handleStatusUpdate}
                    loading={isUpdatingStatus}
                    disabled={!selectedStatus}
                  />
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-outlined p-button-secondary"
                    onClick={() => {
                      setShowStatusForm(false);
                      setSelectedStatus(null);
                      setStatusNotes("");
                    }}
                    disabled={isUpdatingStatus}
                  />
                </div>
              </div>
            )}
          </div>
        </ResolutionPlanCard>

        {/* Right Column: Appeal Letter Draft */}
        {analysis.resolution.appealLetterDraft &&
          analysis.resolution.appealLetterDraft.trim() && (
            <AppealLetterCard>
              <AppealLetterHeader>
                <AppealLetterTitle>Appeal Letter Draft</AppealLetterTitle>
                <AppealLetterActions>
                  <Button
                    label={copied ? "Copied!" : "Copy Text"}
                    icon={copied ? "pi pi-check" : "pi pi-copy"}
                    className="p-button-outlined p-button-secondary"
                    style={{ borderRadius: "9999px" }}
                    onClick={() =>
                      handleCopy(analysis.resolution.appealLetterDraft)
                    }
                  />
                </AppealLetterActions>
              </AppealLetterHeader>
              <AppealLetterBody>
                <AppealLetterContent>
                  {analysis.resolution.appealLetterDraft}
                </AppealLetterContent>
              </AppealLetterBody>
            </AppealLetterCard>
          )}
      </ResolutionGrid>

      {/* Similar Claims AI Difference Analysis */}
      <DifferenceSection>
        <DifferenceSectionTitle>
          Similar Claims Comparison
        </DifferenceSectionTitle>
        <DifferenceSectionDescription>
          AI comparison of this underpaid claim against similar claims from the
          same payer that were paid correctly, highlighting likely causes of the
          payment difference.
        </DifferenceSectionDescription>

        {isDifferenceLoading && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 0",
              color: "#86868b",
              fontSize: "13px",
            }}
          >
            <div style={{ width: "16px", height: "16px", flexShrink: 0 }}>
              <ProgressSpinner
                style={{ width: "16px", height: "16px" }}
                strokeWidth="5"
              />
            </div>
            <span>Analyzing differences with AI...</span>
          </div>
        )}

        {!isDifferenceLoading && differenceData && (
          <DifferenceAnalysisBox>
            <DifferenceAnalysisText>
              {differenceData.analysis}
            </DifferenceAnalysisText>
          </DifferenceAnalysisBox>
        )}

        {!isDifferenceLoading && isDifferenceError && (
          <Message
            severity="warn"
            text="Unable to generate similar claims comparison. This claim may not have similar correctly-paid claims available."
            style={{ width: "100%" }}
          />
        )}
      </DifferenceSection>

      {/* Prompt Version and Regenerate Button */}
      <RegenerateButtonWrapper>
        {promptVersion && (
          <TimestampText>
            Generated with prompt v{promptVersion.version} (
            {new Date(promptVersion.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
            )
          </TimestampText>
        )}
        <Button
          label="Regenerate Analysis"
          icon="pi pi-refresh"
          onClick={handleRegenerate}
          className="p-button-secondary p-button-outlined"
          disabled={isLoading}
        />
      </RegenerateButtonWrapper>
    </ContentWrapper>
  );
}
